import { ButtonGroup, IconButton, Pagination } from '@chakra-ui/react';
import CustomerTable from '@client/page/management/customer/customer-list/component/customer-table.tsx';
import SearchInput from '@client/page/management/customer/customer-list/component/search-bar.tsx';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useCompanyId } from '@packages/core/company/focused-company-context.ts';
import usePagination from '@packages/core/utils/pagination/use-pagination';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import CardTableContent from '@packages/ui/card/card-content-table.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import Card from '@packages/ui/card/card.tsx';
import { ContentLayout, PrimaryContent } from '@packages/ui/content-layout.tsx';
import Loading from '@packages/ui/loading.tsx';
import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import useCustomerCount from './logic/use-customer-count';
import useCustomersV2 from './logic/use-customers-v2';

export interface CustomerData {
  id: string;
  customerVariants: {
    id: string;
    name: string;
    email: string;
    phone: string;
    createdAt: string | null | undefined;
  }[];
}

export default function CustomerList() {
  const companyId = useCompanyId();
  const { t } = useTranslate();

  const { data: quantityData, loading: quantityLoading, error: quantityError } = useCustomerCount(companyId);
  const paginationInfo = usePagination(quantityData?.customerCount ?? 0, {
    limit: 20,
  });
  const { data, loading, error } = useCustomersV2(
    companyId,
    paginationInfo.info.current * paginationInfo.info.perPage,
    paginationInfo.info.perPage,
  );
  const [filterData, setFilterData] = useState<CustomerData[]>([]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setFilterData(
      data.customers_v2.map((item) => ({
        id: item.id,
        customerVariants: item.customerVariants.map((variant) => ({
          id: variant.id,
          name: variant.name,
          email: variant.email,
          phone: variant.phone,
          createdAt: variant.createdAt,
        })),
      })),
    );
  }, [data, setFilterData]);

  if (loading || quantityLoading) {
    return <Loading />;
  }

  if (!data) {
    return <>Data is empty</>;
  }

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  if (error || quantityError) {
    return <>Somethingh went wrong while fetching data</>;
  }

  const handleSearch = (searchString: string) => {
    const customer = data.customers_v2.map((item) => ({
      id: item.id,
      customerVariants: item.customerVariants.map((variant) => ({
        id: variant.id,
        name: variant.name,
        email: variant.email,
        phone: variant.phone,
        createdAt: variant.createdAt,
      })),
    }));
    setFilterData(
      customer.filter((item) => {
        if (item.customerVariants.find((variant) => variant.name.toLowerCase().includes(searchString.toLowerCase()))) {
          return true;
        }
        if (item.customerVariants.find((variant) => variant.phone.toLowerCase().includes(searchString.toLowerCase()))) {
          return true;
        }
        if (item.customerVariants.find((variant) => variant.email.toLowerCase().includes(searchString.toLowerCase()))) {
          return true;
        }
      }),
    );
  };

  return (
    <ContentLayout>
      <PrimaryContent>
        <Card>
          <CardHeader title={t('customer.list.header', 'Customer')}>
            <SearchInput onChange={handleSearch} />
            <SecondaryButton>{t('common.button.export', 'Export')}</SecondaryButton>
          </CardHeader>
          <CardTableContent>
            <CustomerTable items={filterData} />
          </CardTableContent>
          <Pagination.Root
            count={paginationInfo.info.size}
            pageSize={paginationInfo.info.perPage}
            page={paginationInfo.info.current + 1}
            onPageChange={(e) => {
              paginationInfo.updateCurrent(e.page - 1);
            }}
            px={2}
            py={4}
            display={'flex'}
            justifyContent={'end'}
          >
            <ButtonGroup variant="ghost" size="sm">
              <Pagination.PrevTrigger asChild>
                <IconButton>
                  <ChevronLeftIcon />
                </IconButton>
              </Pagination.PrevTrigger>

              <Pagination.Items
                render={(page) => (
                  <IconButton
                    p={2.5}
                    border={'1px solid'}
                    color={paginationInfo.info.current == page.value - 1 ? 'white' : 'gray.500'}
                    fontWeight={'semibold'}
                    borderColor={'gray.300'}
                    bgColor={paginationInfo.info.current == page.value - 1 ? 'gray.500' : 'white'}
                    variant={{ base: 'ghost', _selected: 'outline' }}
                  >
                    {page.value}
                  </IconButton>
                )}
              />

              <Pagination.NextTrigger asChild>
                <IconButton>
                  <ChevronRightIcon />
                </IconButton>
              </Pagination.NextTrigger>
            </ButtonGroup>
          </Pagination.Root>
        </Card>
      </PrimaryContent>
    </ContentLayout>
  );
}
